import React from 'react';
const { updateSyncErrors, touch } = require('redux-form/lib/actions').default;

import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import { isAnEmail } from '@eb/validators';
import { AlertChunky } from '@eb/eds-iconography';
import { lazyGettext } from '@eb/lazy-gettext';

import {
    SIGN_IN_METHOD_FACEBOOK_LOGIN,
    SIGN_IN_METHOD_APPLE_LOGIN,
    SIGN_IN_METHOD_PASSWORD,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
} from '@eb/social-login-facebook';

import {
    setAccountExistent,
    updateFormType,
    showAuthNotification,
    clearAuthNotification,
    setShowModal,
    redirectToSignUp,
    redirectToLogIn,
} from './auth';

import {
    SOCIAL_LOOK_UP_PATH,
    LOOKUP_SOCIAL_USER_EXISTENT,
    LOOKUP_SOCIAL_USER_UNEXISTENT,
    LOG_IN_FORM,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_FACEBOOK_SOFT_ACCOUNT,
} from '../constants';

import { LOOKUP_ERROR_SPEC } from '../api/index';

import {
    ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
    NOT_EMAIL_ASSOCIATED,
    SIGNUP_EMAIL_EXISTS,
    CREATE_ACCOUNT,
} from '../constants/messages';

import { GA_FACEBOOK, GA_APPLE, GA_EVENTBRITE } from '../constants/analytics';

import {
    trackAccountAlreadyConnectedWithFB,
    trackEnterEmailSoftAccount,
    trackEnterEmailExistingAccount,
    trackEnterEmailNewAccount,
    trackAccountConnectedToFacebookAndApple,
    trackAccountConnectedToApple,
    trackContinueCreateAccountClick,
    trackContinueCreateAccountExists,
    trackContinueCreateAccountNew,
} from './gaTracking';

import { isLoginProviderEnabled } from '../utils';

import { lookupUser as lookupUserApi } from '../api';

/**  ACTION CREATORS  **/
export const SET_SIGNUP_SECOND_STEP = 'SET_SIGNUP_SECOND_STEP';
export const setSignupSecondStep = (payload) => ({
    type: SET_SIGNUP_SECOND_STEP,
    payload,
});
export const SET_SOCIAL_CONNECTED = 'setSocialConnected';
export const setSocialConnected = (socialConnected) => ({
    type: SET_SOCIAL_CONNECTED,
    payload: socialConnected,
});

export const SET_LOOKUP_LOADING = 'setLookupLoading';
export const setLookupLoading = (payload) => ({
    type: SET_LOOKUP_LOADING,
    payload: payload,
});

/** ACTIONS **/
/**
 * Main logic for the lookup action
 * @param {Object} arguments - arguments passed to the function.
 * @param {string} arguments.email - The email of the user.
 * @param {string} arguments.socialProvider - Undefined if not coming from a social button, otherwise 'apple' or 'facebook'.
 * @param {string} arguments.sub - Special key for apple, Undefined if not coming from apple.
 * @param {boolean} arguments.isSignup - Signals if coming from the 'creat account' flow.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 */

export const lookupUser = ({
    email,
    socialProvider,
    sub,
    isSignup = false,
}) => (dispatch, getState) => {
    if (email && isAnEmail(email)) {
        if (!socialProvider && !isSignup) {
            // Only save execution in EB Login
            dispatch(setLookupLoading(true));
        }
        return lookupUserApi(email, socialProvider, sub)
            .then((response) => {
                if (socialProvider) {
                    return handleLookupUserResponseSocial({
                        response,
                        dispatch,
                        getState,
                        email,
                        socialProvider,
                    }).catch((error) => Promise.reject(error));
                } else {
                    if (isSignup) {
                        trackContinueCreateAccountClick({
                            state: getState(),
                            sourceProvider: GA_EVENTBRITE,
                        });
                        // For signup, different response
                        dispatch(handleSignupUserResponse(response, email));
                    } else {
                        dispatch(handleLookupUserResponse(response, email));
                        dispatch(setLookupLoading(false));
                    }
                }
            })
            .catch((error) => {
                dispatch(setLookupLoading(false));
                throw error;
            });
    } else {
        dispatch(clearAuthNotification());

        dispatch(setAccountExistent(''));

        if (socialProvider) {
            return Promise.reject();
        }
    }
};

/** RESPONSE HANDLERS **/
/**
 * Handles the response from the api if the user is trying to Login without using the Social Buttons
 * @param {Object} response - response from the 'lookupUserApi'.
 * @param {string} email - The email of the user.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 */
export const handleLookupUserResponse = (response, email) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const {
        authentication: { socialSSOSettings },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));
    dispatch(clearAuthNotification());
    dispatch(setSocialConnected(null));
    if (!!userId) {
        if (includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
            trackEnterEmailExistingAccount({ state });
        } else if (
            hasFbAndAppleAccountOnly({
                socialSSOSettings,

                signInMethods,
            })
        ) {
            trackAccountConnectedToFacebookAndApple({ state });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
                    iconType: <AlertChunky />,
                }),
            );
            dispatch(setSocialConnected([GA_FACEBOOK, GA_APPLE]));
        } else if (hasFbAccount({ socialSSOSettings, signInMethods })) {
            trackAccountAlreadyConnectedWithFB({
                state,
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
                    iconType: <AlertChunky />,
                }),
            );
            dispatch(setSocialConnected([GA_FACEBOOK]));
        } else if (hasAppleAccount({ socialSSOSettings, signInMethods })) {
            trackAccountConnectedToApple({ state });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
                    iconType: <AlertChunky />,
                }),
            );
            dispatch(setSocialConnected([GA_APPLE]));
        } else {
            trackEnterEmailSoftAccount({ state });
            // dispatch action to open popup soft account
            dispatch(setShowModal(true));
        }
    } else {
        trackEnterEmailNewAccount({ state });
        dispatch(
            showAuthNotification({
                message: NOT_EMAIL_ASSOCIATED,
                iconType: null,
                onClick: redirectToSignUp,
                buttonCTA: CREATE_ACCOUNT,
            }),
        );
        dispatch(
            updateSyncErrors(LOG_IN_FORM, LOOKUP_ERROR_SPEC['USER_NOT_EXISTS']),
        );
        dispatch(touch(LOG_IN_FORM, 'email'));
    }
};

/** RESPONSE HANDLERS FOR SIGNUP **/
/**
 * Handles the response from the api if the user is trying to Signup without using the Social Buttons
 * @param {Object} response - response from the 'lookupUserApi'.
 * @param {string} email - The email of the user.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 */
export const handleSignupUserResponse = (response, email) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const { user_id: userId } = response;
    dispatch(setAccountExistent(email));
    dispatch(clearAuthNotification());
    if (userId) {
        trackContinueCreateAccountExists({
            state,
            sourceProvider: GA_EVENTBRITE,
        });
        dispatch(
            showAuthNotification({
                message: SIGNUP_EMAIL_EXISTS,
                iconType: null,
                onClick: redirectToLogIn,
                buttonCTA: lazyGettext('Log in'),
            }),
        );
    } else {
        trackContinueCreateAccountNew({
            state,
            sourceProvider: GA_EVENTBRITE,
        });
        dispatch(setSignupSecondStep(true));
    }
};

/**
 * Handles the response from the api if the user is trying to Login or Signup using the Social Buttons
 * @param {Object} response - response from the 'lookupUserApi'.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 * @param {string} email - The email of the user.
 * @param {string} socialProvider - Undefined if not coming from a social button, otherwise 'apple' or 'facebook'.
 */
export const handleLookupUserResponseSocial = ({
    response,
    dispatch,
    getState,
    email,
    socialProvider,
}) => {
    const state = getState();
    const {
        authentication: {
            socialSSOSettings,
            socialAuthenticationData: { apple },
        },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;
    const socialProviderChecker = socialProviderCheckers[socialProvider];
    let sourceProvider = GA_FACEBOOK;

    if (!isEmpty(apple)) {
        sourceProvider = GA_APPLE;
    }

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));
    if (!!userId) {
        // If user has account linked to this social provider we return and continue with the login
        if (
            socialProviderChecker &&
            socialProviderChecker({ socialSSOSettings, signInMethods })
        ) {
            // trackEventFromState(state, {
            //     action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE,
            //     category: GA_AUTH_CATEGORY,
            // });

            // We reject the function so it enters the catch on appleLogin.
            // Probably it is better to provide some kind of error to ensure a proper treatment
            return Promise.reject({ message: LOOKUP_SOCIAL_USER_EXISTENT });
        }

        if (includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
            trackEnterEmailExistingAccount({ state });

            // Returning this will redirect us to LINK_ACCOUNTS_LOG_IN_PATH, that is the look familiar
            // page with the new design.
            return Promise.reject({ message: LOOKUP_SOCIAL_USER_EXISTENT });
        } else if (hasFbAccount({ socialSSOSettings, signInMethods })) {
            trackAccountAlreadyConnectedWithFB({
                state,
                sourceProvider,
            });

            return Promise.reject({
                message: LOOKUP_SOCIAL_USER_EXISTENT_IN_FACEBOOK_SOFT_ACCOUNT,
            });
        } else if (hasAppleAccount({ socialSSOSettings, signInMethods })) {
            trackAccountConnectedToApple({ state });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
                    iconType: <AlertChunky />,
                }),
            );

            return Promise.resolve();
        } else {
            //User has a Soft account
            // We reject the function so it enters the catch on appleLogin.
            // Probably it is better to provide some kind of error to ensure a proper treatment
            return Promise.reject({
                message: LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
            });
        }
    } else {
        // trackEventFromState(state, {
        //     action: GA_ENTER_EMAIL_EVENT,
        //     category: GA_AUTH_CATEGORY,
        //     label: GA_NEW_ACCT_LABEL,
        // });
        dispatch(updateFormType(SOCIAL_LOOK_UP_PATH));

        // The account is not existent so we ask the user what should he do.
        // Rejecting the promise will allow us to trigger the socialLoginAction
        // and store whatever we need in redis.
        return Promise.reject({ message: LOOKUP_SOCIAL_USER_UNEXISTENT });
    }
};

/** UTILS **/
/**
 * Checks if the email of the user is assosiated with a Facebook AND an Apple account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasFbAndAppleAccountOnly = ({
    socialSSOSettings,

    signInMethods,
}) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    ) &&
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    ) &&
    isEqual(signInMethods, [
        SIGN_IN_METHOD_FACEBOOK_LOGIN,
        SIGN_IN_METHOD_APPLE_LOGIN,
    ]);

/**
 * Checks if the email of the user is assosiated with a Facebook account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasFbAccount = ({ socialSSOSettings, signInMethods }) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    ) && includes(signInMethods, SIGN_IN_METHOD_FACEBOOK_LOGIN);

/**
 * Checks if the email of the user is assosiated with an Apple account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasAppleAccount = ({ socialSSOSettings, signInMethods }) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    ) && includes(signInMethods, SIGN_IN_METHOD_APPLE_LOGIN);

const socialProviderCheckers = {
    ['apple']: hasAppleAccount,
    ['facebook']: hasFbAccount,
    // ['google']: hasGoogleAccount,
};
